import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";
import { Eyebrow } from "@/components/Motion";
import LeafDecor from "@/components/LeafDecor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", company: "", country: "", inquiry_type: "buyer", product_line: "both", message: "" };

function Field({ label, name, value, onChange, type = "text", testid, required }) {
  return (
    <label className="block group">
      <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-forest/80 group-focus-within:text-gold transition-colors duration-300">
        {label}{required && <span className="text-gold"> *</span>}
      </span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border-b border-forest/25 py-3 font-serif text-xl text-forest placeholder-forest/25 focus:outline-none focus:border-gold transition-colors duration-300"
      />
    </label>
  );
}

export default function Contact() {
  const { c } = useLang();
  const ct = c.contact;
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success(ct.toastSuccess);
      setForm(empty);
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      toast.error(ct.toastError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 sm:py-40 bg-[#DCD6C1] border-t border-forest/10 overflow-hidden">
      <LeafDecor layers={[
        { img: "corner", className: "top-0 right-0 w-[460px] sm:w-[560px] -mt-20 -mr-20 rotate-[95deg]", opacity: 0.32 },
        { img: "bunch", className: "bottom-0 left-0 w-[340px] -ml-14 -mb-10 -rotate-6", opacity: 0.3 },
        { img: "side", className: "top-1/4 left-1/3 h-[300px] rotate-[12deg] hidden xl:block", opacity: 0.14 },
      ]} />
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Eyebrow testid="contact-eyebrow">{ct.eyebrow}</Eyebrow>
          <h2 className="mt-6 font-serif font-light leading-[1.02] tracking-[-0.02em] text-forest text-5xl sm:text-6xl">
            {ct.heading.pre}<span className="text-gold italic">{ct.heading.em}</span>
          </h2>
          <p className="mt-6 font-sans text-sm sm:text-base leading-relaxed text-forest/95 max-w-md">{ct.subcopy}</p>

          <div className="mt-12 space-y-6 border-t border-forest/15 pt-8">
            <div>
              <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold mb-2">{ct.directorsLabel}</div>
              <div className="font-serif text-2xl text-forest">{ct.directors.join(" · ")}</div>
            </div>
            <div>
              <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold mb-2">{ct.marketsLabel}</div>
              <div className="font-sans text-sm text-forest/95">{ct.markets}</div>
            </div>
            <div>
              <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-gold mb-2">{ct.emailLabel}</div>
              <a href={`mailto:${ct.email}`} className="font-sans text-sm text-forest/95 link-underline">{ct.email}</a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
            <Field label={ct.fields.name} name="name" value={form.name} onChange={onChange} required testid="contact-name" />
            <Field label={ct.fields.email} name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
            <Field label={ct.fields.company} name="company" value={form.company} onChange={onChange} testid="contact-company" />
            <Field label={ct.fields.country} name="country" value={form.country} onChange={onChange} testid="contact-country" />

            <div className="sm:col-span-2">
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-forest/80">{ct.iamLabel}</span>
              <div className="mt-3 flex flex-wrap gap-2">
                {ct.types.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    data-testid={`contact-type-${t.id}`}
                    onClick={() => setForm((f) => ({ ...f, inquiry_type: t.id }))}
                    className={`font-sans text-xs uppercase tracking-[0.16em] px-5 py-2.5 border transition-colors duration-300 ${
                      form.inquiry_type === t.id ? "bg-gold text-forest border-gold" : "border-forest/25 text-forest/60 hover:border-gold"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="sm:col-span-2 block group">
              <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-forest/80 group-focus-within:text-gold transition-colors duration-300">
                {ct.fields.message} <span className="text-gold">*</span>
              </span>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={4}
                data-testid="contact-message"
                className="mt-2 w-full bg-transparent border-b border-forest/25 py-3 font-serif text-xl text-forest placeholder-forest/25 focus:outline-none focus:border-gold transition-colors duration-300 resize-none"
              />
            </label>

            <div className="sm:col-span-2">
              <motion.button
                type="submit"
                disabled={loading || done}
                data-testid="contact-submit"
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-3 bg-forest text-cream font-sans text-xs uppercase tracking-[0.2em] px-9 py-4 hover:bg-gold hover:text-forest transition-colors duration-400 disabled:opacity-70"
              >
                {done ? (
                  <>{ct.received} <Check size={16} /></>
                ) : loading ? (
                  ct.sending
                ) : (
                  <>{ct.send} <ArrowRight size={16} className="transition-transform duration-400 group-hover:translate-x-1" /></>
                )}
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
